import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PROFILE_UPDATED_EVENT } from '../utils/events';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './CompanySettingsPage.css';

const DEFAULT_FORM = {
  companyName: '',
  companyEmail: '',
  companyDomain: '',
  organizationNumber: '',
  logoUrl: '',
  brandColor: '#6366f1',
  billingContactName: '',
  billingContactEmail: '',
  billingContactPhone: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingPostalCode: '',
  billingCity: '',
  billingCountry: 'NO',
  billingReference: '',
};

const normalizeColor = (value) => {
  if (!value) return '#6366f1';
  const trimmed = value.trim();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};

const normalizeCountry = (value) => {
  if (!value) return 'NO';
  return value.trim().toUpperCase() || 'NO';
};

const CompanySettingsPage = () => {
  const { selectedCompany, user, refreshCompanies } = useAuth();
  const navigate = useNavigate();
  const companyId = selectedCompany?.id || null;
  const isOwner = Boolean(selectedCompany?.isOwner) || Boolean(selectedCompany?.isSuperAdminAccess) || Boolean(user?.isAdmin);

  // Profile form state
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [completion, setCompletion] = useState(null);

  // Load profile
  useEffect(() => {
    if (!companyId || !isOwner) {
      setForm(DEFAULT_FORM);
      setCompletion(null);
      setLoading(false);
      if (!companyId) setError('Velg et selskap for å administrere innstillingene.');
      return undefined;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await DashboardApi.getCustomerProfile(companyId);
        if (!mounted) return;
        setForm({
          companyName: profile.companyName || '',
          companyEmail: profile.companyEmail || '',
          companyDomain: profile.companyDomain || '',
          organizationNumber: profile.organizationNumber || '',
          logoUrl: profile.logoUrl || '',
          brandColor: normalizeColor(profile.brandColor),
          billingContactName: profile.billingContactName || '',
          billingContactEmail: profile.billingContactEmail || '',
          billingContactPhone: profile.billingContactPhone || '',
          billingAddressLine1: profile.billingAddressLine1 || '',
          billingAddressLine2: profile.billingAddressLine2 || '',
          billingPostalCode: profile.billingPostalCode || '',
          billingCity: profile.billingCity || '',
          billingCountry: normalizeCountry(profile.billingCountry),
          billingReference: profile.billingReference || '',
        });
        setCompletion(profile.profileCompletion || null);
      } catch (profileError) {
        if (!mounted) return;
        const message = profileError.message || 'Kunne ikke hente selskapsdata.';
        setError(message);
        notifyError(message, { title: 'Kunne ikke hente innstillinger', source: 'Selskap' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [companyId, isOwner]);

  useEffect(() => {
    if (success) {
      notifySuccess(success, { source: 'Selskap' });
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!companyId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        organization_number: form.organizationNumber,
        company_name: form.companyName,
        company_email: form.companyEmail,
        company_domain: form.companyDomain,
        logo_url: form.logoUrl,
        brand_color: normalizeColor(form.brandColor),
        billing_contact_name: form.billingContactName,
        billing_contact_email: form.billingContactEmail,
        billing_contact_phone: form.billingContactPhone,
        billing_address_line1: form.billingAddressLine1,
        billing_address_line2: form.billingAddressLine2,
        billing_postal_code: form.billingPostalCode,
        billing_city: form.billingCity,
        billing_country: normalizeCountry(form.billingCountry),
        billing_reference: form.billingReference,
      };
      const updated = await DashboardApi.updateCustomerProfile(companyId, payload);
      setForm({
        companyName: updated.companyName || '',
        companyEmail: updated.companyEmail || '',
        companyDomain: updated.companyDomain || '',
        organizationNumber: updated.organizationNumber || '',
        logoUrl: updated.logoUrl || '',
        brandColor: normalizeColor(updated.brandColor),
        billingContactName: updated.billingContactName || '',
        billingContactEmail: updated.billingContactEmail || '',
        billingContactPhone: updated.billingContactPhone || '',
        billingAddressLine1: updated.billingAddressLine1 || '',
        billingAddressLine2: updated.billingAddressLine2 || '',
        billingPostalCode: updated.billingPostalCode || '',
        billingCity: updated.billingCity || '',
        billingCountry: normalizeCountry(updated.billingCountry),
        billingReference: updated.billingReference || '',
      });
      try {
        await refreshCompanies();
      } catch {
        // Keep local success state even if company context refresh fails.
      }
      setCompletion(updated.profileCompletion || null);
      setSuccess('Endringene ble lagret.');
      window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
    } catch (updateError) {
      const message = updateError.message || 'Kunne ikke lagre endringene.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke lagre innstillinger', source: 'Selskap' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveCompany = async () => {
    if (!companyId || archiving || saving) return;

    const companyName = selectedCompany?.name || form.companyName || 'selskapet';
    const confirmed = window.confirm(
      `Er du sikker på at du vil slette (arkivere) ${companyName}?\n\nDette fjerner selskapet fra oversikten din, men data blir liggende lagret.`,
    );
    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      'Bekreft: Dette kan ikke angres i dashbordet.\n\nTrykk OK for å arkivere selskapet.',
    );
    if (!doubleConfirmed) return;

    setArchiving(true);
    setError(null);
    setSuccess(null);
    try {
      await DashboardApi.archiveCompany(companyId);
      try {
        await refreshCompanies();
      } catch {
        // Ignore refresh errors here; user will be redirected anyway.
      }
      navigate('/selskap', { replace: true });
    } catch (archiveError) {
      const message = archiveError.message || 'Kunne ikke arkivere selskapet.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke arkivere selskap', source: 'Selskap' });
    } finally {
      setArchiving(false);
    }
  };

  const logoPreview = useMemo(() => {
    if (!form.logoUrl || !form.logoUrl.startsWith('http')) return '';
    return form.logoUrl;
  }, [form.logoUrl]);

  if (!isOwner) {
    return (
      <div className="page settings-page">
        <div className="settings-restricted">
          <div className="settings-restricted-icon">🔒</div>
          <h2>Ingen tilgang</h2>
          <p>Bare eiere kan endre selskapsinnstillinger.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page settings-page">
      <header className="settings-header">
        <div className="settings-header-content">
          <p className="settings-eyebrow">Selskap</p>
          <h1>Innstillinger</h1>
          <p className="settings-subtitle">
            Administrer {selectedCompany?.name || 'selskapet'} - profil og fakturering.
          </p>
        </div>
      </header>

      {/* Profile Form */}
      <form className="settings-form" onSubmit={handleSubmit}>
        {loading ? (
          <div className="settings-loading">
            <div className="settings-spinner" />
            <p>Laster selskapsdata...</p>
          </div>
        ) : (
          <>
            <section className="settings-card">
              <header className="settings-card-header">
                <h2>Grunnleggende opplysninger</h2>
                <p>Informasjon som vises i dashbordet og på fakturaen.</p>
              </header>
              <div className="settings-grid">
                <div className="settings-field">
                  <label htmlFor="companyName">Selskapsnavn</label>
                  <input
                    id="companyName"
                    type="text"
                    value={form.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="Skriv inn selskapsnavn"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="organizationNumber">Organisasjonsnummer</label>
                  <input
                    id="organizationNumber"
                    type="text"
                    value={form.organizationNumber}
                    onChange={(e) => handleChange('organizationNumber', e.target.value)}
                    placeholder="123 456 789"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="companyEmail">Firmapost</label>
                  <input
                    id="companyEmail"
                    type="email"
                    value={form.companyEmail}
                    onChange={(e) => handleChange('companyEmail', e.target.value)}
                    placeholder="support@firma.no"
                  />
                  <small className="settings-field-hint">
                    Brukes for viktige meldinger til selskapet utenfor kontaktflyter.
                  </small>
                </div>
                <div className="settings-field settings-field--domain">
                  <label htmlFor="companyDomain">Selskapsdomene</label>
                  <input
                    id="companyDomain"
                    type="text"
                    value={form.companyDomain}
                    onChange={(e) => handleChange('companyDomain', e.target.value)}
                    placeholder="firma.no"
                  />
                  <small className="settings-field-hint">
                    Brukes som låst e-postsuffix for KI Chat kontaktflyter (`alias@dette-domenet`).
                  </small>
                </div>
                <div className="settings-field">
                  <label htmlFor="brandColor">Profilfarge</label>
                  <div className="color-picker-inline">
                    <div className="color-swatch-btn">
                      <input
                        type="color"
                        value={normalizeColor(form.brandColor)}
                        onChange={(e) => handleChange('brandColor', e.target.value)}
                      />
                    </div>
                    <input
                      id="brandColor"
                      type="text"
                      value={normalizeColor(form.brandColor)}
                      onChange={(e) => handleChange('brandColor', e.target.value)}
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
                <div className="settings-field full-width">
                  <label htmlFor="logoUrl">Logo URL</label>
                  <input
                    id="logoUrl"
                    type="url"
                    value={form.logoUrl}
                    onChange={(e) => handleChange('logoUrl', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                  {logoPreview && (
                    <div className="logo-preview">
                      <img src={logoPreview} alt="Logo" />
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="settings-card">
              <header className="settings-card-header">
                <h2>Faktureringskontakt</h2>
                <p>Personen vi kontakter ved fakturering.</p>
              </header>
              <div className="settings-grid">
                <div className="settings-field">
                  <label htmlFor="billingContactName">Kontaktperson</label>
                  <input
                    id="billingContactName"
                    type="text"
                    value={form.billingContactName}
                    onChange={(e) => handleChange('billingContactName', e.target.value)}
                    placeholder="Navn"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingContactEmail">E-post</label>
                  <input
                    id="billingContactEmail"
                    type="email"
                    value={form.billingContactEmail}
                    onChange={(e) => handleChange('billingContactEmail', e.target.value)}
                    placeholder="faktura@firma.no"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingContactPhone">Telefon</label>
                  <input
                    id="billingContactPhone"
                    type="tel"
                    value={form.billingContactPhone}
                    onChange={(e) => handleChange('billingContactPhone', e.target.value)}
                    placeholder="+47 000 00 000"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingReference">Referanse / PO-nummer</label>
                  <input
                    id="billingReference"
                    type="text"
                    value={form.billingReference}
                    onChange={(e) => handleChange('billingReference', e.target.value)}
                    placeholder="Valgfritt"
                  />
                </div>
              </div>
            </section>

            <section className="settings-card">
              <header className="settings-card-header">
                <h2>Faktureringsadresse</h2>
                <p>Adressen som brukes på fakturaen.</p>
              </header>
              <div className="settings-grid">
                <div className="settings-field full-width">
                  <label htmlFor="billingAddressLine1">Adresse linje 1</label>
                  <input
                    id="billingAddressLine1"
                    type="text"
                    value={form.billingAddressLine1}
                    onChange={(e) => handleChange('billingAddressLine1', e.target.value)}
                    placeholder="Gate / postboks"
                  />
                </div>
                <div className="settings-field full-width">
                  <label htmlFor="billingAddressLine2">Adresse linje 2</label>
                  <input
                    id="billingAddressLine2"
                    type="text"
                    value={form.billingAddressLine2}
                    onChange={(e) => handleChange('billingAddressLine2', e.target.value)}
                    placeholder="Etasjenr., c/o, osv. (valgfritt)"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingPostalCode">Postnummer</label>
                  <input
                    id="billingPostalCode"
                    type="text"
                    value={form.billingPostalCode}
                    onChange={(e) => handleChange('billingPostalCode', e.target.value)}
                    placeholder="0000"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingCity">Poststed</label>
                  <input
                    id="billingCity"
                    type="text"
                    value={form.billingCity}
                    onChange={(e) => handleChange('billingCity', e.target.value)}
                    placeholder="By / sted"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="billingCountry">Land</label>
                  <input
                    id="billingCountry"
                    type="text"
                    value={form.billingCountry}
                    onChange={(e) => handleChange('billingCountry', e.target.value.toUpperCase())}
                    placeholder="NO"
                    maxLength={2}
                  />
                </div>
              </div>
            </section>

            <div className="settings-form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </>
        )}
      </form>

      {!loading && companyId && (
        <section className="settings-card settings-danger-zone">
          <header className="settings-card-header">
            <h2>Fareområde</h2>
            <p>
              Hvis du ikke lenger skal bruke dette selskapet kan du slette det. Sletting betyr at selskapet arkiveres (ikke fjernes fra databasen),
              slik at du kan opprette flere selskaper.
            </p>
          </header>
          <div className="settings-danger-zone-actions">
            <button
              type="button"
              className="btn btn-danger-outline"
              onClick={handleArchiveCompany}
              disabled={archiving || saving}
            >
              {archiving ? 'Sletter...' : 'Slett selskap'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default CompanySettingsPage;
